/**
 * Represents whether a payment method is accepted/enabled for a catalog
 */
export enum PaymentAcceptanceStatus {
    /** Payment method is accepted and available */
    Accepted = 'on',
    /** Payment method is not accepted or disabled */
    NotAccepted = 'off',
}

export type PaymentOption = {
    [PaymentOptionKeys.AmericanExpressAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.BitcoinAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.DinersClubAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.DiscoverAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.ECheckAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.JCBAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.MoneyOrderAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.OtherAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.CashOnDeliveryAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.PaymentDescriptionRequired]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.PayPalAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.PersonalChecksAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.VisaMastercardAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.WireTransferAccepted]?: PaymentAcceptanceStatus;
    [PaymentOptionKeys.ShippingLocation]?: string;
    [PaymentOptionKeys.BuyerPaysActualShipping]?: PaymentAcceptanceStatus;
};

export enum PaymentOptionKeys {
    AmericanExpressAccepted = 'po-amexaccepted',
    BitcoinAccepted = 'po-bitcoinaccepted',
    BuyerPaysActualShipping = 'sp-buyerpaysshippingactual',
    CashOnDeliveryAccepted = 'po-paymentcod',
    DinersClubAccepted = 'po-dinersaccepted',
    DiscoverAccepted = 'po-discoveraccepted',
    ECheckAccepted = 'po-echeckaccepted',
    JCBAccepted = 'po-jcbaccepted',
    MoneyOrderAccepted = 'po-moneyorderaccepted',
    OtherAccepted = 'po-otheraccepted',
    PayPalAccepted = 'po-paypalaccepted',
    PaymentDescriptionRequired = 'po-paymentseedescription',
    PersonalChecksAccepted = 'po-personalchecksaccepted',
    ShippingLocation = 'so-shipwhere',
    VisaMastercardAccepted = 'po-visamastercardaccepted',
    WireTransferAccepted = 'po-wireaccepted',
}

export type PaymentMethodInfo = {
    category: 'creditCard' | 'alternative' | 'shipping';
    creditCardBrand?: 'visa' | 'mastercard' | 'amex' | 'discover' | 'diners' | 'jcb';
    displayName: string;
    icon?: string;
    key: PaymentOptionKeys;
};

const PAYMENT_METHODS_CONFIG: PaymentMethodInfo[] = [
    {
        category: 'creditCard',
        creditCardBrand: 'visa',
        displayName: 'Visa / Mastercard',
        key: PaymentOptionKeys.VisaMastercardAccepted,
    },
    {
        category: 'creditCard',
        creditCardBrand: 'amex',
        displayName: 'American Express',
        key: PaymentOptionKeys.AmericanExpressAccepted,
    },
    {
        category: 'creditCard',
        creditCardBrand: 'discover',
        displayName: 'Discover',
        key: PaymentOptionKeys.DiscoverAccepted,
    },
    {
        category: 'creditCard',
        creditCardBrand: 'diners',
        displayName: 'Diners Club',
        key: PaymentOptionKeys.DinersClubAccepted,
    },
    {
        category: 'creditCard',
        creditCardBrand: 'jcb',
        displayName: 'JCB',
        key: PaymentOptionKeys.JCBAccepted,
    },
    {
        category: 'alternative',
        displayName: 'PayPal',
        key: PaymentOptionKeys.PayPalAccepted,
    },
    {
        category: 'alternative',
        displayName: 'Personal Check',
        key: PaymentOptionKeys.PersonalChecksAccepted,
    },
    {
        category: 'alternative',
        displayName: 'Cash on Delivery',
        key: PaymentOptionKeys.CashOnDeliveryAccepted,
    },
    {
        category: 'alternative',
        displayName: 'Money Order',
        key: PaymentOptionKeys.MoneyOrderAccepted,
    },
    {
        category: 'alternative',
        displayName: 'Wire Transfer (Bank Payment)',
        key: PaymentOptionKeys.WireTransferAccepted,
    },
    {
        category: 'alternative',
        displayName: 'eCheck',
        key: PaymentOptionKeys.ECheckAccepted,
    },
    {
        category: 'alternative',
        displayName: 'Bitcoin',
        key: PaymentOptionKeys.BitcoinAccepted,
    },
    {
        category: 'alternative',
        displayName: 'Other Payment Methods',
        key: PaymentOptionKeys.OtherAccepted,
    },
];

/**
 * Hardcoded payment methods for live-payments
 * Live payments always show all credit cards and Wire Transfer only
 */
export const LIVE_PAYMENT_CREDIT_CARDS = PAYMENT_METHODS_CONFIG.filter((method) => method.category === 'creditCard');

export const LIVE_PAYMENT_WIRE_TRANSFER = PAYMENT_METHODS_CONFIG.filter(
    (method) => method.key === PaymentOptionKeys.WireTransferAccepted
);

/**
 * Utility functions for working with PaymentOption
 */
/**
 * Get enabled alternative payment methods only
 */
export const getEnabledAlternativeMethods = (paymentOptions?: PaymentOption): PaymentMethodInfo[] => {
    return getEnabledMethods(paymentOptions).filter((method) => method.category === 'alternative');
};

/**
 * Get enabled credit card methods only
 */
export const getEnabledCreditCards = (paymentOptions?: PaymentOption): PaymentMethodInfo[] => {
    return getEnabledMethods(paymentOptions).filter((method) => method.category === 'creditCard');
};
/**
 * Get enabled payment methods from the PaymentOption object
 */
const getEnabledMethods = (paymentOptions?: PaymentOption): PaymentMethodInfo[] => {
    if (!paymentOptions) {
        return [];
    }

    return PAYMENT_METHODS_CONFIG.filter((method) => {
        const value = paymentOptions[method.key];
        return value === PaymentAcceptanceStatus.Accepted;
    });
};

/**
 * Check if a specific payment method is enabled
 */
export const isPaymentMethodEnabled = (paymentOptions?: PaymentOption, key?: PaymentOptionKeys): boolean => {
    if (!paymentOptions || !key) {
        return false;
    }
    return paymentOptions[key] === PaymentAcceptanceStatus.Accepted;
};
